import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import PropTypes from "prop-types";
import {
  User,
  Building2,
  Building,
  Lock,
  Bell,
  FileText,
  Calendar,
  CalendarCheck,
  AlarmClock,
  UserCheck,
  Briefcase,
  Users,
  MapPin,
  IdCard,
  Shield,
  LayoutDashboard,
  Image as ImageIcon,
  Menu as MenuIcon,
} from "lucide-react";
import { hasPermission } from "../../utils/permissionUtils";

// Section order: GENERAL → SYSTEM CONFIGURATION → PERSONAL → ADMINISTRATION → APPEARANCE.
const SECTION_ORDER = [
  "General",
  "System Configuration",
  "Personal",
  "Administration",
  "Appearance",
];

const SettingsSidebar = ({ role: roleProp }) => {
  const [collapsed, setCollapsed] = useState(true);

  const role = (roleProp || localStorage.getItem("role") || "")
    .toLowerCase()
    .trim();

  const basePath =
    role === "admin"
      ? "/admin/settings"
      : role === "hr"
        ? "/hr/settings"
        : "/member/settings";

  // All settings tabs. Excluded-from-redesign tabs (Letter Templates, Profile
  // Generator, Personalise Dashboard, Backgrounds) remain listed and routable —
  // per the brief, they're kept but their inner content isn't restyled.
  const sidebarItems = [
    {
      key: "companyinfo",
      label: "Company Information",
      Icon: Building2,
      category: "General",
      permission: "manage_company_info",
      path: "company-info",
      roles: ["admin", "hr"],
    },
    {
      key: "password",
      label: "Password",
      Icon: Lock,
      category: "General",
      permission: "view_settings",
      path: "password-management",
      roles: ["admin", "hr", "member"],
    },
    {
      key: "lettertemplates",
      label: "Letter Templates",
      Icon: FileText,
      category: "General",
      permission: "manage_letter_templates",
      path: "letter-templates",
      roles: ["admin", "hr"],
    },

    {
      key: "calendars",
      label: "Calendar Settings",
      Icon: Calendar,
      category: "System Configuration",
      permission: "manage_calendar_settings",
      path: "calendar",
      roles: ["admin", "hr"],
    },
    {
      key: "shift",
      label: "Shift Management",
      Icon: AlarmClock,
      category: "System Configuration",
      permission: "manage_calendar_settings",
      path: "shift-management",
      roles: ["admin", "hr"],
    },
    {
      key: "attendance",
      label: "Attendance Settings",
      Icon: UserCheck,
      category: "System Configuration",
      permission: "manage_calendar_settings",
      path: "attendance",
      roles: ["admin", "hr"],
    },
    {
      key: "departments",
      label: "Departments",
      Icon: Building,
      category: "System Configuration",
      permission: "manage_company_info",
      path: "departments",
      roles: ["admin", "hr"],
    },
    {
      key: "jobtitles",
      label: "Job Titles",
      Icon: Briefcase,
      category: "System Configuration",
      permission: "manage_company_info",
      path: "job-titles",
      roles: ["admin", "hr"],
    },
    {
      key: "employeetypes",
      label: "Employee Types",
      Icon: Users,
      category: "System Configuration",
      permission: "manage_company_info",
      path: "employee-types",
      roles: ["admin", "hr"],
    },
    {
      key: "worklocations",
      label: "Work Locations",
      Icon: MapPin,
      category: "System Configuration",
      permission: "manage_company_info",
      path: "work-locations",
      roles: ["admin", "hr"],
    },
    {
      key: "employmentstatuses",
      label: "Employment Statuses",
      Icon: User,
      category: "System Configuration",
      permission: "manage_company_info",
      path: "employment-statuses",
      roles: ["admin", "hr"],
    },
    {
      key: "profilepicgenerator",
      label: "Profile Generator",
      Icon: IdCard,
      category: "System Configuration",
      permission: "manage_profile_generator",
      path: "profile-generator",
      roles: ["admin", "hr"],
    },
    {
      key: "notifications",
      label: "Notifications",
      Icon: Bell,
      category: "Personal",
      permission: "view_settings",
      path: "notifications",
      roles: ["admin", "hr", "member"],
    },
    {
      // The route existed but nothing linked to it, so the page was only
      // reachable by typing the URL.
      key: "myleaverequests",
      label: "My Leave Requests",
      Icon: CalendarCheck,
      category: "Personal",
      permission: "apply_leave",
      path: "my-leave-requests",
      roles: ["admin", "hr", "member"],
    },
    {
      key: "rolesacess",
      label: "Roles and Permissions",
      Icon: Shield,
      category: "Administration",
      permission: "manage_roles_permissions",
      path: "roles-access",
      roles: ["admin", "hr"],
    },

    {
      key: "personalise",
      label: "Personalise Dashboard",
      Icon: LayoutDashboard,
      category: "Appearance",
      permission: "view_settings",
      path: "personalise",
      roles: ["admin", "hr", "member"],
    },
    {
      key: "backgrounds",
      label: "Backgrounds",
      Icon: ImageIcon,
      category: "Appearance",
      permission: "manage_backgrounds",
      path: "backgrounds",
      roles: ["admin", "hr"],
    },
  ];

  const visibleItems = sidebarItems.filter((item) => {
    const hasPerm = hasPermission(item.permission);
    const hasRole = !item.roles || item.roles.includes(role);
    return hasPerm && hasRole;
  });

  const grouped = visibleItems.reduce((acc, item) => {
    (acc[item.category] ||= []).push(item);
    return acc;
  }, {});

  const sections = SECTION_ORDER.filter((c) => grouped[c]?.length);

  return (
    <nav className={`em-settings-sidebar ${collapsed ? "is-collapsed" : ""}`}>
      <button
        type="button"
        className="em-settings-mobtoggle"
        onClick={() => setCollapsed((c) => !c)}
      >
        <span>Settings Menu</span>
        <MenuIcon size={18} />
      </button>

      <div className="em-settings-sectionwrap">
        {sections.map((category) => (
          <div key={category} className="em-settings-section">
            <div className="em-settings-section-label">{category}</div>
            {grouped[category].map((item) => (
              <NavLink
                key={item.key}
                to={`${basePath}/${item.path}`}
                onClick={() => setCollapsed(true)}
                className={({ isActive }) =>
                  `em-settings-navitem ${isActive ? "active" : ""}`
                }
              >
                {({ isActive }) => (
                  <>
                    <item.Icon size={15} style={{ flexShrink: 0 }} />
                    <span className="em-settings-navlabel">{item.label}</span>
                    {isActive && <span className="em-settings-navdot" />}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
};

SettingsSidebar.propTypes = {
  role: PropTypes.string,
};

export default SettingsSidebar;
